import Qs from "qs";
import { ITagGroup } from "@edgetier/watchtower-types";

/**
 * Find the first tag group. This is what the page will use as a default.
 * @param tagGroups All tag groups from the backend.
 * @returns         First tag group or null if there are none.
 */
const selectFirstTagGroup = (tagGroups?: ITagGroup[]) => {
    return Array.isArray(tagGroups) && tagGroups.length > 0 ? tagGroups[0] : null;
};

/**
 * Take either a tag group from the URL or the first one provided from the backend.
 * @param search    URL search string.
 * @param tagGroups All tag groups from the backend.
 * @returns         Selected tag group.
 */
export const selectTagGroup = (search: string, tagGroups: ITagGroup[] | undefined) => {
    if (!Array.isArray(tagGroups)) {
        return;
    }

    const { tagGroupId } = Qs.parse(search, { ignoreQueryPrefix: true });

    return tagGroups.find((item) => item.tagGroupId === tagGroupId) ?? selectFirstTagGroup(tagGroups);
};

/**
 * When the user clicks on a tag, either add or remove it to the URL query string. If the tag group is not in the URL at
 * this point it must also be added, otherwise the URL could contain a tag that isn't on-screen.
 * @param tagGroupId    Tag group the tag belongs to.
 * @param tags          Currently selected tags.
 * @param tagId         Clicked tag id.
 * @param search        Current URL query string.
 * @returns             New query string.
 *
 * @example
 * // Returns "?tagGroup=code&tagId=complaint":
 * toggleUrlTag("code", [], "complaint", "");
 *
 * // Returns "?tagGroup=code&tagId=sales":
 * toggleUrlTag("code", [], "complaint", "?tagId=sales&tagId=complaint");
 */
export const toggleUrlTag = (tagGroupId: string | undefined, tags: string[], tagId: string, search: string) => {
    const newTagIds = tags.includes(tagId) ? tags.filter((oldTag) => oldTag !== tagId) : tags.concat(tagId);
    const parameters = Qs.parse(search, { ignoreQueryPrefix: true });

    return Qs.stringify({ ...parameters, tagGroupId, tagId: newTagIds }, { arrayFormat: "repeat" });
};
